/**
 * Poslať do Cut Rite (KOLO 129).
 *
 * Okno ukáže, čo bráni odoslaniu: materiál bez kódu v cenníku (tabule
 * v Cut Rite sú naimportované z cenníka, bez kódu by dielec ostal bez
 * tabule), nulový rozmer alebo množstvo. Priraďovať sa nič nemusí —
 * kód dosky v cenníku je ten istý ako v Cut Rite.
 *
 * Po odoslaní si súbor vezme pomocník na PC s Cut Rite, naimportuje ho
 * a optimalizuje. Od KOLA 145 okno ukazuje **kroky** (odoslané → prevzaté
 * → optimalizované → na píle) a pri dokončení hlásenie aj zatvorené —
 * user: *„daj mi info, že všetko sa poslalo, loader, niečo"*. Po
 * optimalizácii tlačidlo **Odoslať na pílu** (pomocník spustí SAWLINK)
 * a od KOLA 154 **Vytlačiť nárezové plány** — pomocník ich z Cut Rite
 * vytlačí do PDF, okno ho potom otvorí na tlač. Od KOLA 161 je okno po
 * krokoch (stav → Poslať → Po optimalizácii → odkaz na súbor); PDF plánov
 * ukáže od KOLA 163 ten istý náhľad ako Výstupy (`PdfNahladDialog`).
 * Od KOLA 178 nad tlačidlom **Odrezky pre túto zákazku** (`CutriteOdrezky`)
 * — pomocník ich pridá do Seznamu tabulí úlohy a po optimalizácii povie,
 * či ich plán použil.
 *
 * Od KOLA 207 je každá poslaná časť zákazky **vlastný zoznam** v Cut Rite
 * (user: *„pre Cut Rite sú to 2 zákazky, v Rozpočte jedna"*). Pri viac
 * variantách riadok **Varianty:** vyberie, ktoré idú do zoznamu (predvolená
 * otvorená záložka), okno povie meno zoznamu a vymenuje ďalšie zoznamy
 * zákazky. Pozri `docs/rozpocet/CUTRITE.md`.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { menoZoznamu, vetaZmeny } from "@/components/zakazka/PasCutrite";
import { KapsuleVariant } from "@/components/zakazka/KapsuleVariant";
import { nazovVarianty } from "@/lib/variantyZakazky";
import {
  AlertTriangle,
  CheckCircle2,
  Circle,
  Download,
  FileText,
  Loader2,
  Printer,
  Scissors,
  Send,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "@/components/ui/use-toast";
import { extractErrorMessage } from "@/api/client";
import {
  bezi,
  beziImport,
  beziPila,
  beziPlany,
  stiahniCutriteCsv,
  useCutriteKontrola,
  useCutriteLinky,
  useCutritePosledne,
  usePosliDoCutrite,
  usePosliNaPilu,
  useVytlacPlany,
} from "@/api/hooks/useCutrite";
import type { CutriteLinka, CutriteOdoslanie, Varianta } from "@/api/schemas";
import { PdfNahladDialog } from "@/components/zakazka/PdfNahladDialog";
import {
  CutriteOdrezky,
  pocetOdrezkov,
  vetaOdrezku,
} from "@/components/zakazka/CutriteOdrezky";
import { fetchCutritePlanyPdf, type PdfVystup } from "@/lib/pdfDownload";
import { formatDateTime } from "@/lib/format";

function pocetDielcov(n: number): string {
  if (n === 1) return "1 dielec";
  if (n >= 2 && n <= 4) return `${n} dielce`;
  return `${n} dielcov`;
}

/** „19 dielcov" alebo „19 dielcov + 1 odrezok" (KOLO 178). */
function dielceAOdrezky(dielcov: number, odrezkov: number): string {
  return odrezkov > 0
    ? `${pocetDielcov(dielcov)} + ${pocetOdrezkov(odrezkov)}`
    : pocetDielcov(dielcov);
}

function otazkaZnova(o: CutriteOdoslanie): string {
  const kedy = o.dokoncene_at ? ` ${formatDateTime(o.dokoncene_at)}` : "";
  return (
    `Táto zákazka už bola optimalizovaná v Cut Rite${kedy} (${o.nazov_suboru}). ` +
    "Poslať znova? Zoznam dielcov v Cut Rite sa prepíše novým."
  );
}

function pocetChyb(n: number): string {
  if (n === 1) return "1 chyba";
  if (n >= 2 && n <= 4) return `${n} chyby`;
  return `${n} chýb`;
}

const STAV: Record<string, string> = {
  caka: "Čaká na pomocníka na PC s Cut Rite.",
  prevzate: "Pomocník ho spracúva v Cut Rite…",
  hotovo: "Naimportované a optimalizované v Cut Rite.",
  chyba: "Cut Rite hlási chybu:",
};

const STAV_PILY: Record<string, string> = {
  caka: "Čaká na odoslanie na pílu.",
  prevzate: "Posiela sa na pílu…",
  hotovo: "Odoslané na pílu.",
  chyba: "Na pílu sa to poslať nepodarilo:",
  // Odoslanie z píly, ktorého nový odrezok sa nepoužil (KOLO 186)
  netreba: "Na pílu netreba — plán sa nezmenil.",
};

const STAV_PLANOV: Record<string, string> = {
  caka: "Plány čakajú na pomocníka na PC s Cut Rite.",
  prevzate: "Pomocník tlačí nárezové plány do PDF…",
  hotovo: "Nárezové plány sú pripravené.",
  chyba: "Nárezové plány sa nepodarilo vytlačiť:",
};

type StavKroku = "hotovo" | "bezi" | "chyba" | "dalej";

interface Krok {
  nazov: string;
  stav: StavKroku;
  cas?: string | null;
}

/** Kroky odoslania (KOLO 145) — kde je zákazka na ceste do Cut Rite a na pílu. */
export function krokyOdoslania(o: CutriteOdoslanie): Krok[] {
  const kroky: Krok[] = [
    { nazov: "Odoslané z Rozpočtu", stav: "hotovo", cas: o.created_at },
    {
      nazov: "Pomocník na PC prevzal",
      stav: o.stav === "caka" ? "bezi" : "hotovo",
      cas: o.prevzate_at,
    },
    {
      nazov: "Naimportované a optimalizované v Cut Rite",
      stav:
        o.stav === "hotovo"
          ? "hotovo"
          : o.stav === "chyba"
            ? "chyba"
            : o.stav === "prevzate"
              ? "bezi"
              : "dalej",
      cas: o.dokoncene_at,
    },
  ];
  if (o.pila_stav === "netreba") {
    // Odrezok z píly sa nepoužil — na píle ostáva ten istý plán (KOLO 186)
    kroky.push({
      nazov: "Na pílu netreba — odrezok sa nepoužil",
      stav: "hotovo",
      cas: o.pila_dokoncene_at,
    });
  } else if (o.pila_stav) {
    kroky.push({
      nazov: "Odoslané na pílu",
      stav:
        o.pila_stav === "hotovo" ? "hotovo" : o.pila_stav === "chyba" ? "chyba" : "bezi",
      cas: o.pila_dokoncene_at ?? o.pila_ziadost_at,
    });
  }
  if (o.plany_stav) {
    kroky.push({
      nazov: "Nárezové plány v PDF",
      stav:
        o.plany_stav === "hotovo" ? "hotovo" : o.plany_stav === "chyba" ? "chyba" : "bezi",
      cas: o.plany_dokoncene_at ?? o.plany_ziadost_at,
    });
  }
  return kroky;
}

function IkonaKroku({ stav }: { stav: StavKroku }): JSX.Element {
  if (stav === "hotovo") return <CheckCircle2 className="h-4 w-4 text-green-600" />;
  if (stav === "chyba") return <AlertTriangle className="h-4 w-4 text-destructive" />;
  if (stav === "bezi") return <Loader2 className="h-4 w-4 animate-spin text-primary" />;
  return <Circle className="h-4 w-4 text-muted-foreground/50" />;
}

/**
 * Chyby z Cut Rite po dielcoch (KOLO 149) — user: „chcem, aby tie chyby si
 * mi vypísal v tej tabuľke". Chyby hrán sú len počet: Cut Rite nemá
 * databázu hrán a pomocník kvôli nim nezastaví.
 */
function TabulkaChyb({ o }: { o: CutriteOdoslanie }): JSX.Element {
  return (
    <div className="mt-2 space-y-2">
      <table className="w-full text-xs" data-testid="cutrite-chyby-tabulka">
        <thead>
          <tr className="text-left text-muted-foreground">
            <th className="py-1 pr-2 font-medium">Dielec</th>
            <th className="py-1 pr-2 font-medium">Chyba</th>
            <th className="py-1 font-medium">Hodnota</th>
          </tr>
        </thead>
        <tbody>
          {o.chyby_cutrite.map((c, i) => (
            <tr key={i} className="border-t border-destructive/20 align-top">
              <td className="py-1 pr-2">
                {c.kod || c.nazov ? (
                  <>
                    <span className="font-mono">{c.kod}</span> {c.nazov}
                    {c.zostava ? (
                      <div className="text-muted-foreground">{c.zostava}</div>
                    ) : null}
                  </>
                ) : (
                  "—"
                )}
              </td>
              <td className="py-1 pr-2">{c.chyba}</td>
              <td className="py-1 font-mono">{c.hodnota}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {o.sprava ? (
        <details>
          <summary className="cursor-pointer select-none text-xs text-muted-foreground hover:text-foreground">
            Celý text z Cut Rite
          </summary>
          <pre className="mt-1 whitespace-pre-wrap text-xs">{o.sprava}</pre>
        </details>
      ) : null}
    </div>
  );
}

/**
 * Na PC beží starší pomocník než v repe (KOLO 156) — user hlásil tú istú
 * chybu hrán trikrát, lebo oprava platí až po skopírovaní pomocnik.ps1.
 */
function StaryPomocnik(): JSX.Element {
  return (
    <p
      className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-sm text-amber-900 dark:text-amber-200"
      data-testid="cutrite-stary-pomocnik"
    >
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <span>
        Na PC s Cut Rite beží starý pomocník. Stiahni nový (Nastavenia → Prepojenia →
        Pomocník pre Cut Rite), skopíruj z neho <span className="font-mono">pomocnik.ps1</span>{" "}
        do <span className="font-mono">C:\Tandem\cutrite_pomocnik</span> a pošli
        zákazku znova.
      </span>
    </p>
  );
}

function StavOdoslania({ o }: { o: CutriteOdoslanie }): JSX.Element {
  const chybaImportu = o.stav === "chyba" && !o.pila_stav;
  // Cut Rite hlásil len hrany a starý pomocník kvôli nim zastavil — nie je
  // to chyba, hrany sa ignorujú (KOLO 156: „vypíš hlásenia všetky okrem hrany")
  const lenHrany =
    chybaImportu && o.chyby_cutrite.length === 0 && o.ignorovane_hrany > 0;
  const chybaPily = o.pila_stav === "chyba";
  const chyba = (o.stav === "chyba" && !lenHrany) || chybaPily;
  const nadpis = lenHrany
    ? "Zastavené len kvôli hranám — tie sa ignorujú."
    : o.pila_stav
      ? STAV_PILY[o.pila_stav]
      : STAV[o.stav];
  const sprava = o.pila_stav ? o.pila_sprava : o.sprava;
  return (
    <div
      className={
        "rounded-md border p-3 text-sm " +
        (chyba ? "border-destructive/50 bg-destructive/5" : "bg-muted/40")
      }
      data-testid="cutrite-stav"
    >
      <div className="font-medium">{nadpis ?? o.stav}</div>
      {chybaImportu && o.chyby_cutrite.length > 0 ? (
        <TabulkaChyb o={o} />
      ) : lenHrany ? (
        <div className="mt-1 space-y-1 text-xs" data-testid="cutrite-len-hrany">
          {/* Hrany hlási Cut Rite pri importe aj pri optimalizácii (KOLO 157) —
              starší pomocník zastavil v tom kroku, kde ich videl */}
          <p>
            {o.sprava?.startsWith("IMPORT.ERR")
              ? `Cut Rite hlásil len chyby hrán (${o.ignorovane_hrany}) pri importe — zákazka je naimportovaná, ale neoptimalizovaná.`
              : `Cut Rite hlásil len chyby hrán (${o.ignorovane_hrany}) pri optimalizácii — starší pomocník kvôli nim zastavil.`}{" "}
            Pošli ju znova.
          </p>
          {o.sprava ? (
            <details>
              <summary className="cursor-pointer select-none text-muted-foreground hover:text-foreground">
                Celý text z Cut Rite
              </summary>
              <pre className="mt-1 whitespace-pre-wrap">{o.sprava}</pre>
            </details>
          ) : null}
        </div>
      ) : sprava && (chyba || o.pila_stav === "hotovo" || o.pila_stav === "netreba") ? (
        <pre className="mt-1 whitespace-pre-wrap text-xs">{sprava}</pre>
      ) : null}
      {o.pridane_tabule.length > 0 ? (
        <p className="mt-1 text-xs text-muted-foreground" data-testid="cutrite-pridane-tabule">
          Pridané do Knižnice tabúľ Cut Rite:{" "}
          <span className="font-mono">{o.pridane_tabule.join(", ")}</span>
        </p>
      ) : null}
      {o.ignorovane_hrany > 0 && !lenHrany ? (
        <p className="mt-1 text-xs text-muted-foreground" data-testid="cutrite-hrany">
          Ignorované upozornenia na hrany: {o.ignorovane_hrany} (Cut Rite nemá
          databázu hrán).
        </p>
      ) : null}
      {o.plany_stav && o.plany_stav !== "hotovo" ? (
        <p
          className={
            "mt-1 text-xs " + (o.plany_stav === "chyba" ? "text-destructive" : "")
          }
          data-testid="cutrite-plany"
        >
          {STAV_PLANOV[o.plany_stav] ?? o.plany_stav}
          {o.plany_stav === "chyba" && o.plany_sprava ? ` ${o.plany_sprava}` : ""}
        </p>
      ) : null}
      {o.stav === "hotovo" && o.odrezky.length > 0 ? (
        <ul className="mt-1 space-y-0.5 text-xs" data-testid="cutrite-odrezky-vysledok">
          {o.odrezky.map((r) => (
            <li
              key={r.tabula}
              className={
                r.pouzite === 0
                  ? "font-medium text-amber-700 dark:text-amber-300"
                  : "text-muted-foreground"
              }
            >
              {vetaOdrezku(r)}
            </li>
          ))}
        </ul>
      ) : null}
      <ol className="mt-2 space-y-1" aria-label="Kroky odoslania">
        {krokyOdoslania(o).map((k) => (
          <li
            key={k.nazov}
            className={
              "flex items-center gap-2 " +
              (k.stav === "dalej" ? "text-muted-foreground" : "")
            }
            data-krok={k.stav}
          >
            <IkonaKroku stav={k.stav} />
            <span>{k.nazov}</span>
            {k.cas && k.stav !== "dalej" && k.stav !== "bezi" ? (
              <span className="text-xs text-muted-foreground">
                {formatDateTime(k.cas)}
              </span>
            ) : null}
          </li>
        ))}
      </ol>
      <div className="mt-2 text-xs text-muted-foreground">
        {o.nazov_suboru} ·{" "}
        {dielceAOdrezky(o.riadkov, o.odrezky.reduce((s, r) => s + r.pocet, 0))}
        {o.autor ? ` · poslal ${o.autor}` : ""}
        {/* Odoslanie z programu Odrezky — na pílu ide samo (KOLO 179);
            z kancelárie znova po inom zozname (KOLO 207) */}
        {o.pila_auto
          ? o.pila_z_kancelarie
            ? " · po optimalizácii ide na pílu samo"
            : " z píly, na pílu ide samo"
          : ""}
      </div>
    </div>
  );
}

/**
 * Hlásenie pri dokončení (KOLO 145) — aj keď je okno zatvorené; komponent
 * je stále pripojený vo `VystupyMenu`. Od KOLA 207 pre každý zoznam
 * zákazky (posledné odoslanie každého), nie len pre posledné odoslanie.
 */
function useHlaseniaPriDokonceni(odoslania: readonly CutriteOdoslanie[] | undefined): void {
  const predtym = useRef<Map<string, CutriteOdoslanie> | null>(null);
  useEffect(() => {
    if (!odoslania) return;
    const stare = predtym.current;
    predtym.current = new Map(odoslania.map((o) => [o.id, o]));
    if (!stare) return;
    for (const o of odoslania) {
      const p = stare.get(o.id);
      if (p) hlasenia(p, o);
    }
  }, [odoslania]);
}

function hlasenia(p: CutriteOdoslanie, o: CutriteOdoslanie): void {
  // Pridané tabule v hlásení (KOLO 152) — user: „ty si ho vytvoril, len si
  // mi neoznámil"
  const pridane =
    o.pridane_tabule.length > 0
      ? ` Pridané do Knižnice tabúľ Cut Rite: ${o.pridane_tabule.join(", ")}.`
      : "";
  if (beziImport(p) && o.stav === "hotovo") {
    // Nepoužitý odrezok z píly — na pílu nejde, hlásenie povie prečo (KOLO 186)
    const netreba = o.pila_stav === "netreba" && o.pila_sprava ? ` ${o.pila_sprava}` : "";
    toast({
      title: "Zákazka je v Cut Rite",
      description: `${o.nazov_suboru}: naimportované a optimalizované.${netreba}${pridane}`,
    });
  } else if (beziImport(p) && o.stav === "chyba") {
    toast({
      variant: "destructive",
      title: "Cut Rite hlási chybu",
      description:
        o.chyby_cutrite.length > 0
          ? `${o.nazov_suboru}: ${pocetChyb(o.chyby_cutrite.length)} — pozri okno Poslať do Cut Rite.${pridane}`
          : (o.sprava ?? o.nazov_suboru),
    });
  }
  if (beziPlany(p) && o.plany_stav === "hotovo") {
    toast({
      title: "Nárezové plány sú pripravené",
      description: `${o.nazov_suboru}: otvor ich v okne Poslať do Cut Rite.`,
    });
  } else if (beziPlany(p) && o.plany_stav === "chyba") {
    toast({
      variant: "destructive",
      title: "Nárezové plány sa nepodarilo vytlačiť",
      description: o.plany_sprava ?? o.nazov_suboru,
    });
  }
  if (beziPila(p) && o.pila_stav === "hotovo") {
    toast({ title: "Odoslané na pílu", description: o.pila_sprava ?? o.nazov_suboru });
  } else if (beziPila(p) && o.pila_stav === "chyba") {
    toast({
      variant: "destructive",
      title: "Na pílu sa to poslať nepodarilo",
      description: o.pila_sprava ?? o.nazov_suboru,
    });
  }
}

/** Kde je zoznam — riadok v zozname ďalších zoznamov zákazky (KOLO 207). */
export function kratkyStav(o: CutriteOdoslanie): string {
  let stav: string;
  if (o.pila_stav === "hotovo") stav = "na píle";
  else if (o.pila_stav === "netreba") stav = "na píle (plán sa nezmenil)";
  else if (o.pila_stav === "caka" || o.pila_stav === "prevzate") stav = "posiela sa na pílu";
  else if (o.pila_stav === "chyba" || o.stav === "chyba") stav = "chyba";
  else if (o.stav === "hotovo") stav = "optimalizované";
  else stav = "v Cut Rite…";
  return o.zmenene ? `${stav} · zmenené` : stav;
}

/** Ďalšie zoznamy zákazky v Cut Rite — Otvoriť prepne výber okna. */
function DalsieZoznamy({
  linky,
  onOtvor,
}: {
  linky: readonly CutriteLinka[];
  onOtvor: (varianty: string[]) => void;
}): JSX.Element {
  return (
    <section
      className="min-w-0 space-y-1 border-t pt-3"
      aria-label="Ďalšie zoznamy zákazky v Cut Rite"
      data-testid="cutrite-zoznamy"
    >
      <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Ďalšie zoznamy zákazky v Cut Rite
      </h3>
      <ul className="space-y-1 text-sm">
        {linky.map((l) => (
          <li key={l.id} className="flex min-w-0 flex-wrap items-center gap-x-2">
            <span className="font-mono">{menoZoznamu(l.nazov_suboru)}</span>
            {l.varianty.length > 0 ? (
              <span className="text-xs text-muted-foreground">{l.varianty.join(" + ")}</span>
            ) : null}
            <span
              className={
                "text-xs " +
                (l.posledne.zmenene ? "text-amber-700 dark:text-amber-300" : "text-muted-foreground")
              }
            >
              · {kratkyStav(l.posledne)}
            </span>
            {l.varianta_ids.length > 0 ? (
              <Button
                variant="link"
                size="sm"
                className="ml-auto h-auto px-0 text-xs"
                onClick={() => onOtvor(l.varianta_ids)}
              >
                Otvoriť
              </Button>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}

export function CutriteDialog({
  open,
  onOpenChange,
  zakazkaId,
  varianty,
  otvorenaId,
  vyber,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  zakazkaId: string;
  /** varianty zákazky v poradí záložiek (KOLO 207) */
  varianty: readonly Varianta[];
  /** otvorená záložka — predvolený výber */
  otvorenaId: string;
  /** výber z pásu zmeny — okno otvorí tento zoznam */
  vyber?: readonly string[] | null;
}): JSX.Element {
  // Výber vždy v poradí záložiek — z neho je meno zoznamu aj predpona kódov
  const vPoradi = useMemo(
    () =>
      (ids: readonly string[]): string[] => {
        const chcene = new Set(ids);
        const vybrane = varianty.filter((v) => chcene.has(v.id)).map((v) => v.id);
        return vybrane.length > 0 ? vybrane : [otvorenaId];
      },
    [varianty, otvorenaId],
  );
  // Kým user v okne nič nezmení, výber ide z propov — už prvý dopyt po
  // otvorení z pásu je na zoznam z pásu; po zatvorení sa zabudne
  const [vlastny, setVlastny] = useState<string[] | null>(null);
  useEffect(() => {
    if (!open) setVlastny(null);
  }, [open]);
  const vybrane = useMemo(
    () => vlastny ?? vPoradi(vyber?.length ? vyber : [otvorenaId]),
    [vlastny, vPoradi, vyber, otvorenaId],
  );
  const setVybrane = (ids: readonly string[]): void => setVlastny(vPoradi(ids));
  const viacVariant = varianty.length > 1;

  const kontrola = useCutriteKontrola(zakazkaId, vybrane, open);
  const stav = useCutritePosledne(zakazkaId, vybrane, open);
  const linky = useCutriteLinky(zakazkaId);
  const posli = usePosliDoCutrite(zakazkaId, vybrane);
  const naPilu = usePosliNaPilu(zakazkaId, vybrane);
  const plany = useVytlacPlany(zakazkaId, vybrane);
  const [stahuje, setStahuje] = useState(false);
  // Náhľad plánov: `null` = zavretý, `vystup: null` = PDF sa načítava
  const [planyNahlad, setPlanyNahlad] = useState<{ vystup: PdfVystup | null } | null>(
    null,
  );

  const k = kontrola.data?.data;
  const posledne = stav.data ? stav.data.data : k?.posledne;
  // Pri otvorení čerstvé `zmenene` — zákazka sa mohla medzitým upraviť
  useEffect(() => {
    if (open) void stav.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const prebieha = bezi(posledne);
  const odoslania = useMemo(
    () => linky.data?.data?.map((l) => l.posledne),
    [linky.data],
  );
  useHlaseniaPriDokonceni(odoslania);
  const dalsieZoznamy = (linky.data?.data ?? []).filter(
    (l) => k && l.nazov_suboru !== k.nazov_suboru,
  );

  async function odosli(): Promise<void> {
    // Už optimalizovaná zákazka (KOLO 151) — user: „daj mi okno na opýtanie
    // sa, keď si už raz optimalizoval zákazku… či to chcem prepísať"
    // Po zmene rezania (KOLO 153) je nové odoslanie to, čo treba — bez otázky
    if (
      posledne?.stav === "hotovo" &&
      !posledne.zmenene &&
      !window.confirm(otazkaZnova(posledne))
    )
      return;
    try {
      await posli.mutateAsync();
      toast({
        title: "Odoslané do Cut Rite",
        description:
          "Pomocník na PC s Cut Rite si ho vezme do minúty, naimportuje a optimalizuje.",
      });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Do Cut Rite sa to poslať nepodarilo",
        description: extractErrorMessage(err),
      });
      void kontrola.refetch();
    }
  }

  async function odosliNaPilu(): Promise<void> {
    if (!posledne) return;
    if (!window.confirm(`Poslať ${posledne.nazov_suboru} na pílu?`)) return;
    try {
      const r = await naPilu.mutateAsync(posledne.id);
      // Cut Rite medzitým optimalizoval iný zoznam — server tento poslal
      // znova a na pílu pôjde po optimalizácii sám (KOLO 207)
      toast(
        r.data.id !== posledne.id
          ? {
              title: "Pošle sa znova do Cut Rite",
              description: `Po zozname ${menoZoznamu(posledne.nazov_suboru)} Cut Rite optimalizoval iný. Pošle sa znova a po optimalizácii pôjde na pílu sám.`,
            }
          : {
              title: "Posiela sa na pílu",
              description: "Pomocník na PC ho do minúty pošle z Cut Rite na pílu.",
            },
      );
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Na pílu sa to poslať nepodarilo",
        description: extractErrorMessage(err),
      });
      void stav.refetch();
    }
  }

  async function vytlacPlany(): Promise<void> {
    if (!posledne) return;
    try {
      await plany.mutateAsync(posledne.id);
      toast({
        title: "Tlačím nárezové plány",
        description: "Pomocník na PC ich do minúty vytlačí z Cut Rite do PDF.",
      });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Nárezové plány sa nepodarilo vytlačiť",
        description: extractErrorMessage(err),
      });
      void stav.refetch();
    }
  }

  /**
   * Nárezové plány z Cut Rite v tom istom náhľade ako Výstupy → Nárezový
   * plán (KOLO 163) — user: *„chcem, aby sa zhodoval výstup"*. PDF sa
   * stiahne a ukáže ako blob; iframe priamo na API prehliadač odmietol
   * („rozpocty.tandem.sk zamietol pripojenie"), lebo Caddy zakazuje rámy.
   */
  async function otvorPlany(): Promise<void> {
    if (!posledne) return;
    setPlanyNahlad({ vystup: null });
    try {
      const vystup = await fetchCutritePlanyPdf(
        zakazkaId,
        posledne.id,
        posledne.nazov_suboru,
      );
      // Zavreté počas načítania ostane zavreté
      setPlanyNahlad((p) => (p ? { vystup } : p));
    } catch (err) {
      setPlanyNahlad(null);
      toast({
        variant: "destructive",
        title: "Nárezové plány sa nepodarilo otvoriť",
        description: extractErrorMessage(err),
      });
      void stav.refetch();
    }
  }

  async function stiahni(): Promise<void> {
    if (!k) return;
    setStahuje(true);
    try {
      await stiahniCutriteCsv(zakazkaId, vybrane, k.nazov_suboru);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Súbor sa nepodarilo stiahnuť",
        description: extractErrorMessage(err),
      });
    } finally {
      setStahuje(false);
    }
  }

  // Po optimalizácii (KOLO 161): plány a píla v jednej sekcii; po zmene
  // rezania nič z toho — ani staré PDF (user: „nedovoľ, keď niečo zmením,
  // vytlačiť nárezový plán, ktorý je starý")
  const poOptimalizacii = posledne?.stav === "hotovo" && !posledne.zmenene;
  const planyHotove = posledne?.plany_stav === "hotovo";

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        {/* Okno po krokoch (KOLO 161) — user: „chcem, aby to okno bolo
            prehľadnejšie"; tlačidlá nie sú v jednej zalamovanej pätičke */}
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Poslať do Cut Rite</DialogTitle>
            <DialogDescription>
              Plošné dielce s rozmermi prírezu, ako ich reže porez. Pomocník na PC
              ich v Cut Rite naimportuje a optimalizuje.
            </DialogDescription>
          </DialogHeader>

          {viacVariant ? (
            <div className="min-w-0 space-y-1" data-testid="cutrite-vyber">
              <KapsuleVariant
                ariaLabel="Varianty do Cut Rite"
                varianty={varianty.map((v, i) => ({
                  id: v.id,
                  nazov: nazovVarianty(v),
                  cislo: i + 1,
                  vybrana: vybrane.includes(v.id),
                  druh: v.je_vyrobna ? "výrobná" : null,
                }))}
                onZmena={setVybrane}
              />
              {k ? (
                <p className="text-sm" data-testid="cutrite-zoznam">
                  Zoznam v Cut Rite:{" "}
                  <span className="font-mono font-medium">{menoZoznamu(k.nazov_suboru)}</span>
                  <span className="text-xs text-muted-foreground">
                    {" "}
                    — každá poslaná časť je v Cut Rite zvlášť, iné neprepíše
                  </span>
                </p>
              ) : null}
            </div>
          ) : null}

          {kontrola.isPending ? (
            <p className="text-sm text-muted-foreground">Kontrolujem…</p>
          ) : kontrola.isError || !k ? (
            <p className="text-sm text-destructive">
              {extractErrorMessage(kontrola.error)}
            </p>
          ) : (
            <div className="min-w-0 space-y-3">
              {posledne?.pomocnik_stary ? <StaryPomocnik /> : null}
              {posledne ? <StavOdoslania o={posledne} /> : null}
              {posledne?.zmenene ? (
                <p
                  className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-sm text-amber-900 dark:text-amber-200"
                  data-testid="cutrite-zmenene"
                >
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  {vetaZmeny(
                    posledne,
                    varianty
                      .filter((v) => v.je_cast && vybrane.includes(v.id))
                      .map((v) => v.nazov),
                  )}
                </p>
              ) : null}

              {k.chyby.length > 0 ? (
                <ul className="max-h-80 space-y-2 overflow-y-auto">
                  {k.chyby.map((c) => (
                    <li
                      key={`${c.typ}-${c.material_id ?? ""}`}
                      className="flex items-start gap-2 text-sm text-destructive"
                      data-testid="cutrite-chyba"
                    >
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                      <span>
                        {c.veta}
                        {/* Hrana zlej šírky (KOLO 135) a prírez väčší ako
                            tabuľa (KOLO 193) nesú zoznam dielcov ako
                            materiál bez kódu */}
                        {(c.typ === "bez_kodu" || c.typ === "hrana" || c.typ === "tabula") &&
                        c.dielcov > 0 ? (
                          <span className="text-muted-foreground">
                            {" "}
                            ({pocetDielcov(c.dielcov)})
                          </span>
                        ) : null}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : null}

              <CutriteOdrezky
                zakazkaId={zakazkaId}
                varianty={vybrane}
                odrezky={k.odrezky}
                materialy={k.materialy_odrezkov}
                staryPomocnik={Boolean(posledne?.pomocnik_stary)}
                nadpis={viacVariant ? "Odrezky pre tento zoznam" : undefined}
              />
            </div>
          )}

          <div
            className="flex min-w-0 flex-wrap items-center justify-between gap-2"
            data-testid="cutrite-poslat-riadok"
          >
            {k?.ok ? (
              <p className="min-w-0 text-sm" data-testid="cutrite-ok">
                Pripravené: <span className="font-mono">{k.nazov_suboru}</span>,{" "}
                {dielceAOdrezky(k.riadkov, k.odrezkov)}.
              </p>
            ) : (
              <span />
            )}
            {/* Kým posledné odoslanie beží, ďalšie by išlo do Cut Rite dvakrát
                a prenos na pílu by poslal inú zákazku */}
            <Button
              className="ml-auto"
              onClick={() => void odosli()}
              disabled={!k?.ok || posli.isPending || prebieha}
            >
              {posli.isPending || prebieha ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              {posli.isPending
                ? "Odosielam…"
                : prebieha
                  ? "Čaká v Cut Rite…"
                  : "Poslať do Cut Rite"}
            </Button>
          </div>

          {posledne && poOptimalizacii ? (
            <section
              className="min-w-0 space-y-2 border-t pt-3"
              aria-label="Po optimalizácii"
              data-testid="cutrite-po-optimalizacii"
            >
              <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Po optimalizácii
              </h3>
              <div className="flex flex-wrap gap-2">
                {planyHotove ? (
                  <Button
                    variant="outline"
                    onClick={() => void otvorPlany()}
                    data-testid="cutrite-plany-pdf"
                  >
                    <FileText className="h-4 w-4" />
                    Otvoriť plány (PDF)
                  </Button>
                ) : null}
                {/* Plány z optimalizácie (KOLO 154) — opakovať sa smie */}
                {!prebieha ? (
                  <Button
                    variant="outline"
                    onClick={() => void vytlacPlany()}
                    disabled={plany.isPending || beziPlany(posledne)}
                  >
                    {plany.isPending || beziPlany(posledne) ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Printer className="h-4 w-4" />
                    )}
                    {beziPlany(posledne)
                      ? "Tlačím plány…"
                      : planyHotove
                        ? "Vytlačiť plány znova"
                        : "Vytlačiť nárezové plány"}
                  </Button>
                ) : null}
                {/* Na pílu raz za optimalizáciu a len nezmenenú (KOLO 153); po
                    chybe píly znova */}
                {!prebieha && posledne.pila_stav !== "hotovo" ? (
                  <Button
                    variant="outline"
                    onClick={() => void odosliNaPilu()}
                    disabled={naPilu.isPending}
                  >
                    {naPilu.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Scissors className="h-4 w-4" />
                    )}
                    {naPilu.isPending ? "Posielam na pílu…" : "Odoslať na pílu"}
                  </Button>
                ) : null}
              </div>
            </section>
          ) : null}

          {dalsieZoznamy.length > 0 ? (
            <DalsieZoznamy
              linky={dalsieZoznamy}
              onOtvor={setVybrane}
            />
          ) : null}

          <div
            className="flex flex-wrap items-center gap-x-1 border-t pt-2 text-xs text-muted-foreground"
            data-testid="cutrite-stiahnut"
          >
            <Button
              variant="link"
              size="sm"
              className="h-auto px-0 text-xs text-muted-foreground"
              onClick={() => void stiahni()}
              disabled={!k?.ok || stahuje}
              title="Presne tento súbor sa pošle do Cut Rite — aj na ručný import"
            >
              <Download className="h-3.5 w-3.5" />
              Stiahnuť súbor pre Cut Rite
            </Button>
            <span>(ručný import)</span>
          </div>
        </DialogContent>
      </Dialog>
      <PdfNahladDialog
        vystup={planyNahlad?.vystup ?? null}
        nazov="Nárezové plány z Cut Rite"
        open={planyNahlad !== null}
        onOpenChange={(o) => {
          if (!o) setPlanyNahlad(null);
        }}
      />
    </>
  );
}
